/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.model.reminder.DEFAULT_REMINDER_TEMPLATE
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.settings.generated.resources.Res
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_error_load_failed
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_error_max_out_of_range
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_reminders_error_save_failed

data class ReminderSettingsState(
    val maxReminders: Int = 3,
    val template: String = DEFAULT_REMINDER_TEMPLATE,
    val isLoading: Boolean = true,
    val isSaving: Boolean = false,
    val maxError: UiText? = null,
    val saveError: UiText? = null,
)

sealed class ReminderSettingsEvent {
    data object Saved : ReminderSettingsEvent()
}

sealed class ReminderSettingsAction {
    data class MaxRemindersChanged(val max: Int) : ReminderSettingsAction()
    data class TemplateChanged(val template: String) : ReminderSettingsAction()
    data object SaveClicked : ReminderSettingsAction()
}

class ReminderSettingsViewModel(
    private val profileRepository: ProfileRepository,
) : BaseViewModel<ReminderSettingsState, ReminderSettingsEvent, ReminderSettingsAction>(
    ReminderSettingsState(),
) {

    init {
        load()
    }

    override fun handleAction(action: ReminderSettingsAction) {
        when (action) {
            is ReminderSettingsAction.MaxRemindersChanged ->
                mutableStateFlow.value = state.copy(maxReminders = action.max, maxError = null)
            is ReminderSettingsAction.TemplateChanged ->
                mutableStateFlow.value = state.copy(template = action.template)
            ReminderSettingsAction.SaveClicked -> save()
        }
    }

    private fun load() {
        viewModelScope.launch {
            profileRepository.getCurrentProfile()
                .onSuccess { profile ->
                    mutableStateFlow.value = state.copy(
                        maxReminders = profile.maxReminders,
                        template = profile.reminderTemplate,
                        isLoading = false,
                    )
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isLoading = false,
                        saveError = e.toUiText(Res.string.feature_settings_reminders_error_load_failed),
                    )
                }
        }
    }

    private fun save() {
        val s = state
        val maxErr = if (s.maxReminders !in 1..10) {
            UiText.Resource(Res.string.feature_settings_reminders_error_max_out_of_range)
        } else {
            null
        }
        if (maxErr != null) {
            mutableStateFlow.value = s.copy(maxError = maxErr)
            return
        }

        mutableStateFlow.value = s.copy(isSaving = true, saveError = null)
        viewModelScope.launch {
            profileRepository.updateReminderSettings(
                scheduleDays = DEFAULT_SCHEDULE_DAYS,
                maxReminders = s.maxReminders,
                template = s.template,
            ).onSuccess {
                mutableStateFlow.value = state.copy(isSaving = false)
                sendEvent(ReminderSettingsEvent.Saved)
            }.onFailure { e ->
                mutableStateFlow.value = state.copy(
                    isSaving = false,
                    saveError = e.toUiText(Res.string.feature_settings_reminders_error_save_failed),
                )
            }
        }
    }

    companion object {
        private val DEFAULT_SCHEDULE_DAYS = listOf(3, 7, 14)
    }
}
